import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Upload, Calculator, CheckCircle2, ArrowRight, Play } from 'lucide-react';

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A2B4A] via-[#243552] to-[#1A2B4A]">
      {/* Navigation - Same as Landing */}
      <nav className="fixed top-0 w-full bg-[#1A2B4A]/95 backdrop-blur-sm border-b border-[#4A90E2]/20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-[#4A90E2]" />
              <span className="text-2xl font-heading font-bold text-white">Winglet</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/how-it-works" className="text-[#4A90E2] border-b-2 border-[#4A90E2]">How It Works</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Pricing</Link>
              <Link to="/compare-tools" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Compare</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#4A90E2] transition-colors">About</Link>
              <Link to="/data-privacy" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Data Privacy</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/signup" className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-2 rounded-lg transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
            How Winglet <span className="text-[#4A90E2]">Protects Your Pay</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Three simple steps to never lose money to payroll errors again.
          </p>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Step 1 */}
          <div className="mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4A90E2]/20 rounded-full mb-6">
                  <span className="text-3xl font-heading font-bold text-[#4A90E2]">1</span>
                </div>
                <h2 className="text-4xl font-heading font-bold text-white mb-4">
                  Upload Your Rotation
                </h2>
                <p className="text-xl text-gray-300 mb-6">
                  Paste your rotation text, upload a PDF, or import directly from your airline's scheduling system (ICS format).
                </p>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Supports text paste, PDF upload, ICS import</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Auto-detects rotation type (Line Holder, Reserve, Reroute)</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Works with all major US carriers (Delta, United, American, Southwest, etc.)</span>
                  </div>
                </div>
              </div>
              <div className="bg-[#0F1824] border border-[#4A90E2]/30 rounded-xl p-8">
                <div className="bg-[#1A2B4A] rounded-lg p-6 text-center">
                  <Upload className="h-16 w-16 text-[#4A90E2] mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">Drop your rotation file here or</p>
                  <button className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                    Choose File
                  </button>
                  <p className="text-gray-500 text-sm mt-4">Supports PDF, TXT, ICS formats</p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 bg-[#0F1824] border border-[#4A90E2]/30 rounded-xl p-8">
                <div className="bg-[#1A2B4A] rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-white font-semibold">Processing rotation 4302...</span>
                    <span className="text-[#4A90E2]">✓ Complete</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Parsing flights</span>
                      <span className="text-green-400">✓ 7 legs found</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">FAR 117 analysis</span>
                      <span className="text-green-400">✓ Compliant</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Credit calculation</span>
                      <span className="text-green-400">✓ 22:02 hours</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Timezone conversion</span>
                      <span className="text-green-400">✓ Done</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4A90E2]/20 rounded-full mb-6">
                  <span className="text-3xl font-heading font-bold text-[#4A90E2]">2</span>
                </div>
                <h2 className="text-4xl font-heading font-bold text-white mb-4">
                  We Analyze Everything
                </h2>
                <p className="text-xl text-gray-300 mb-6">
                  Our AI engine parses every flight, calculates credit hours per PWA Section 12, checks FAR 117 compliance, and builds a complete audit trail.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Duty Period Minimum (DPM), Average Daily Guarantee (ADG), Minimum Calendar Day (MCD)</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">1:2 and 1:1.5 credit ratios (day vs overnight)</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Rotation credit (1:3.5), sit pay, extended duty premium</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4A90E2]/20 rounded-full mb-6">
                  <span className="text-3xl font-heading font-bold text-[#4A90E2]">3</span>
                </div>
                <h2 className="text-4xl font-heading font-bold text-white mb-4">
                  Get Instant Answers
                </h2>
                <p className="text-xl text-gray-300 mb-6">
                  See your credit breakdown, compare to company timecard, catch errors before they cost you money.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">
                      <span className="font-semibold text-white">Variance Detection:</span> Automatic comparison when you upload company timecard
                    </span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">
                      <span className="font-semibold text-white">Grievance Support:</span> Export full audit trail with PWA rule citations
                    </span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">
                      <span className="font-semibold text-white">Real-Time Updates:</span> Track flights live with OOOI data from AviationStack
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-[#0F1824] border border-[#4A90E2]/30 rounded-xl p-8">
                <div className="bg-[#1A2B4A] rounded-lg p-6">
                  <h3 className="text-white font-semibold mb-4">Rotation 4302 Summary</h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Your Calculation:</span>
                      <span className="text-white font-semibold">22:02</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Company Timecard:</span>
                      <span className="text-white font-semibold">21:30</span>
                    </div>
                    <div className="border-t border-gray-700 pt-3 flex justify-between">
                      <span className="text-amber-400 font-semibold">Variance:</span>
                      <span className="text-amber-400 font-semibold">+0:32 (owed)</span>
                    </div>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <p className="text-amber-400 text-sm">
                      ⚠️ Potential underpayment detected. Export audit report for grievance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section id="demo-video" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-heading font-bold text-white mb-6">
            See Winglet in Action
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Watch a 90-second walkthrough of how pilots use Winglet to protect their pay.
          </p>
          <div className="aspect-video bg-[#1A2B4A] rounded-xl border border-[#4A90E2]/30 flex items-center justify-center">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#4A90E2] rounded-full mb-4 cursor-pointer hover:bg-[#3A7BC8] transition-colors">
                <Play className="h-10 w-10 text-white ml-1" />
              </div>
              <p className="text-gray-400">Demo video coming soon</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-heading font-bold text-white mb-6">
            Ready to Protect Your Pay?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Try Winglet free for 1.5 months. No credit card required.
          </p>
          <Link 
            to="/signup" 
            className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-12 py-5 rounded-lg text-xl font-semibold transition-all transform hover:scale-105 inline-flex items-center group"
          >
            Start Free Trial
            <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1824] border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Branding */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Plane className="h-6 w-6 text-[#4A90E2]" />
                <span className="text-xl font-heading font-bold text-white">Winglet</span>
              </div>
              <p className="text-gray-400 text-sm">The AI-powered co-pilot for your career.</p>
            </div>
            
            {/* Product Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2] transition-colors">How It Works</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Pricing</Link></li>
                <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Compare</Link></li>
              </ul>
            </div>
            
            {/* Company Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2] transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Contact</Link></li>
                <li><Link to="/data-privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Data Privacy</Link></li>
              </ul>
            </div>
            
            {/* Legal Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 Winglet. Built by pilots, for pilots. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorks;
